import type { ApiClient } from '@carbonwebmail/api-client'
import { designedBody } from '@carbonwebmail/domain'
import { startingDesign } from '@carbonwebmail/email-template/design'
import { lazy, Suspense, useCallback, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { CarbonMark } from '../../ui/CarbonMark.js'
import type { ComposeHandle } from '../../ui/compose/ComposeCanvas.js'
import { Icon } from '../../ui/Icon.js'
import { openPreviewTab } from '../../ui/previewTab.js'
import { ScreenNote } from '../../ui/ScreenNote.js'
import type { Composing } from './useCompose.js'
import { useCompose } from './useCompose.js'

/**
 * Compose: the whole screen, and nothing else on it.
 *
 * Laying out an email needs the width. The application's sidebar, its search bar
 * and its heading are all competing with the thing being made, so this route
 * sits outside the shell entirely — the way back is a button in its own toolbar,
 * which is also the only thing that can ask about unsaved work on the way out.
 *
 * **One screen for a campaign's message and a template's.** They are the same
 * thing; only the row it saves back to differs. Two composers would be two
 * places for the builder, the unsaved guard and the save path to drift.
 *
 * Reached by url and loads what it is composing, so a refresh and a pasted link
 * both work.
 */
/**
 * Fetched when somebody opens Compose, and not before.
 *
 * The editor and its stylesheet are about a megabyte — more than the rest of
 * the application put together. Imported at the top it would be on every page
 * load, including signing in, for a screen most sessions never reach.
 */
const ComposeCanvas = lazy(async () => ({
  default: (await import('../../ui/compose/ComposeCanvas.js')).ComposeCanvas,
}))

/** In the same chunk as the editor: it is no use without one. */
const ComposeCode = lazy(async () => ({
  default: (await import('../../ui/compose/ComposeCode.js')).ComposeCode,
}))

export function ComposePage({ client }: { readonly client: ApiClient }) {
  const { kind = 'campaign', id = '' } = useParams()
  /**
   * Markup the templates screen was given to open with.
   *
   * Router state rather than a query string or storage: it is a same-tab
   * navigation inside one application, the markup can be a whole email, and a
   * url is not where somebody's design belongs.
   */
  const brought = importedFrom(useLocation().state)
  const composing = (kind === 'template' ? 'template' : 'campaign') as Composing
  const navigate = useNavigate()

  const { state, save, fresh, changed, openedWith, rename, commit, design } = useCompose({
    client,
    composing,
    id,
  })

  /** The few commands the toolbar may run. Handed over when the editor is built. */
  const canvas = useRef<ComposeHandle | undefined>(undefined)
  /** The markup as it was when the panel opened; `undefined` while it is shut. */
  const [showing, setShowing] = useState<string | undefined>(undefined)
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop')

  const back = useCallback(() => {
    navigate(composing === 'template' ? '/templates' : `/campaigns/${id}/edit`, { replace: true })
  }, [composing, id, navigate])

  const leave = useCallback(() => {
    // Asked only when there is something to lose. A prompt on every exit is one
    // people learn to dismiss, and then it does not protect the time it was for.
    if (save.status === 'unsaved' && !window.confirm(LEAVING)) return
    back()
  }, [back, save.status])

  const keep = useCallback(async () => {
    const saved = await commit()
    if (saved === undefined) return
    // A template started from the Compose tab has no list to go back to yet, so
    // it goes to the one it has just joined.
    navigate(composing === 'template' ? '/templates' : `/campaigns/${saved}/edit`, {
      replace: true,
    })
  }, [commit, composing, navigate])

  const show = useCallback((next: 'desktop' | 'mobile') => {
    setDevice(next)
    canvas.current?.showOn(next)
  }, [])

  return (
    <div className="relative flex h-screen flex-col bg-bg">
      <header className="flex h-14 flex-none items-center gap-3 border-line border-b bg-surface px-4">
        <button
          type="button"
          onClick={leave}
          aria-label="Back"
          className="rounded-field p-1.5 text-t2 hover:bg-line-2 hover:text-t1"
        >
          <Icon name="back" />
        </button>
        <CarbonMark className="h-[22px] w-[19px] flex-none" />

        {state.status === 'ready' && composing === 'template' ? (
          <>
            {/*
              A template's name and subject are part of composing it — a design
              with no name is one nobody finds again — so they are edited here
              rather than on a form somewhere else. A campaign's are not: those
              belong beside its audience and its from address, and this screen
              only sends back what it read.
            */}
            <input
              type="text"
              value={state.name}
              aria-label="Template name"
              placeholder="Name this template"
              onChange={(event) => rename('name', event.target.value)}
              className="w-[180px] flex-none rounded-field border border-transparent bg-transparent px-2 py-1 font-semibold text-[14px] text-t1 hover:border-line focus:border-line"
            />
            <input
              type="text"
              value={state.subject}
              aria-label="Subject"
              placeholder="Subject line"
              onChange={(event) => rename('subject', event.target.value)}
              className="w-[200px] flex-none rounded-field border border-transparent bg-transparent px-2 py-1 text-[13px] text-t2 hover:border-line focus:border-line"
            />
          </>
        ) : (
          <span className="min-w-0 truncate font-semibold text-[14px] text-t1">
            {state.status === 'ready' ? state.name : fresh ? 'New design' : 'Compose'}
          </span>
        )}
        <Saved save={save} />

        <span className="ml-auto flex items-center gap-1">
          <Tool label="Undo" name="undo" onClick={() => canvas.current?.undo()} />
          <Tool label="Redo" name="redo" onClick={() => canvas.current?.redo()} />
        </span>

        <span className="flex items-center gap-1 rounded-pill bg-surface-2 p-1">
          <Device chosen={device === 'desktop'} label="Desktop" onClick={() => show('desktop')} />
          <Device chosen={device === 'mobile'} label="Mobile" onClick={() => show('mobile')} />
        </span>

        <button
          type="button"
          disabled={state.status !== 'ready'}
          onClick={() => setShowing(canvas.current?.code() ?? '')}
          className="rounded-pill border border-line px-3.5 py-1.5 font-semibold text-[13px] text-t2 hover:text-t1 disabled:opacity-60"
        >
          Code
        </button>
        <button
          type="button"
          disabled={state.status !== 'ready'}
          // A tab of its own, opened straight from the press — a browser only
          // allows that while it is still handling the click.
          onClick={() => {
            if (state.status !== 'ready') return
            openPreviewTab({
              // A subject is required to render, and a template being composed
              // may not have one yet. Its own words stand in rather than
              // refusing to preview.
              subject: state.subject.trim() === '' ? 'Subject line' : state.subject,
              body: designedBody(
                design.current?.html ?? (state.body.kind === 'designed' ? state.body.html : ''),
                '',
              ),
            })
          }}
          className="rounded-pill border border-line px-3.5 py-1.5 font-semibold text-[13px] text-t2 hover:text-t1 disabled:opacity-60"
        >
          Preview
        </button>
        <button
          type="button"
          onClick={leave}
          className="font-medium text-[13px] text-t3 hover:text-t1"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={save.status === 'saving' || state.status !== 'ready'}
          onClick={() => void keep()}
          className="rounded-pill bg-brand px-4 py-2 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
        >
          {save.status === 'saving' ? 'Saving…' : 'Save & quit'}
        </button>
      </header>

      {save.status === 'refused' ? (
        <p className="flex-none border-danger border-b bg-danger-soft px-4 py-2.5 text-[13px] text-danger">
          {save.detail}
        </p>
      ) : null}

      <Body
        composing={composing}
        imported={brought}
        onChange={changed}
        onOpened={openedWith}
        onReady={canvas}
        state={state}
      />

      {showing === undefined ? null : (
        <Suspense fallback={null}>
          <ComposeCode
            code={showing}
            onClose={() => setShowing(undefined)}
            onReplace={(code) => {
              canvas.current?.replace(code)
              setShowing(undefined)
            }}
          />
        </Suspense>
      )}
    </div>
  )
}

type State = ReturnType<typeof useCompose>['state']

function Body({
  composing,
  state,
  imported,
  onChange,
  onOpened,
  onReady,
}: {
  readonly composing: Composing
  readonly state: State
  readonly imported: string | undefined
  readonly onChange: (design: { readonly html: string; readonly source: string }) => void
  readonly onOpened: (design: { readonly html: string; readonly source: string }) => void
  readonly onReady: { current: ComposeHandle | undefined }
}) {
  switch (state.status) {
    case 'opening':
      return <ScreenNote>Opening…</ScreenNote>

    case 'not-found':
      return <ScreenNote>{`That ${composing} is not there any more.`}</ScreenNote>

    /*
     * A queued campaign's message is frozen. Opening an editor over it would
     * offer to change something that has already gone to several thousand
     * people — and two different emails under one campaign id make the send log
     * a record of something that never happened.
     */
    case 'sent':
      return (
        <ScreenNote>
          This campaign has been queued, so its message can no longer be changed.
        </ScreenNote>
      )

    case 'failed':
      return <ScreenNote>We could not open that. Please try again in a moment.</ScreenNote>

    case 'ready':
      return (
        <Suspense fallback={<ScreenNote>Opening Compose…</ScreenNote>}>
          <ComposeCanvas
            // A body that was prose, or a campaign written before the source was
            // kept, opens on a starting document rather than a blank canvas.
            initialHtml={
              state.body.kind === 'designed' && state.body.html !== ''
                ? state.body.html
                : startingDesign(state.body.kind === 'prose' ? state.body.text : '')
            }
            initialSource={state.body.kind === 'designed' ? state.body.source : ''}
            imported={imported}
            onChange={onChange}
            onOpened={onOpened}
            onReady={(handle) => {
              onReady.current = handle
            }}
          />
        </Suspense>
      )
  }
}

/** What the toolbar says about work in progress. Never silent. */
function Saved({ save }: { readonly save: ReturnType<typeof useCompose>['save'] }) {
  switch (save.status) {
    case 'unsaved':
      return <span className="flex-none text-[12px] text-t3">Not saved yet</span>
    case 'saving':
      return <span className="flex-none text-[12px] text-t3">Saving…</span>
    case 'saved':
      return <span className="flex-none text-[12px] text-t3">Saved</span>
    case 'refused':
      return <span className="flex-none text-[12px] text-danger">Not saved</span>
  }
}

function Tool({
  label,
  name,
  onClick,
}: {
  readonly label: string
  readonly name: 'undo' | 'redo'
  readonly onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="rounded-field p-1.5 text-t2 hover:bg-line-2 hover:text-t1"
    >
      <Icon name={name} />
    </button>
  )
}

function Device({
  chosen,
  label,
  onClick,
}: {
  readonly chosen: boolean
  readonly label: string
  readonly onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={chosen}
      onClick={onClick}
      className={`rounded-pill px-3 py-1 font-semibold text-[12px] ${
        chosen ? 'bg-surface text-t1 shadow-sm' : 'text-t3 hover:text-t1'
      }`}
    >
      {label}
    </button>
  )
}

const LEAVING = 'You have changes that are not saved. Leave without saving them?'

/**
 * What the templates screen sent, if it sent anything.
 *
 * Router state is whatever the last navigation put there, including nothing at
 * all after a refresh, so it is checked rather than trusted.
 */
function importedFrom(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null) return undefined
  const { imported } = state as { imported?: unknown }
  return typeof imported === 'string' && imported.trim() !== '' ? imported : undefined
}
